import { PdfDownloadDocument } from '@src/queries/CoaCertificatePDFQuery.generated'
import { downloadPDF } from './certificateUtils'
import { CertificateType } from '@src/types/graphql-types'
import { FormattedMessage, useIntl } from 'react-intl'
import { certificateCOARoute, certificateCOORoute, support } from '@src/routes'
import {
  SnackbarType,
  useGlobalSnackbar,
} from '@src/components/GlobalSnackbar/globalSnackBarContext'
import { Linq } from '@src/components/Linq'
import ApolloClient from 'apollo-client'
import { getLocalizedUrl } from '@src/utils/regional'
import { useState } from 'react'
import { useUserSession } from '@src/utils/useUserSession'

type DownloadCertificatePdfParams = {
  certificateContent: any
  client: ApolloClient<object>
}

type ShowCertificatePdfParams = {
  certificateContent: any
  isCOA: boolean
}

export const useDocumentationCertificate = () => {
  const [pdfLoading, setPDFLoading] = useState(false)
  const [certificateNotFoundModal, setCertificateNotFoundModal] =
    useState<CertificateType | null>(null)

  const { userSession } = useUserSession()
  const { setGlobalSnackbarState } = useGlobalSnackbar()
  const intl = useIntl()

  const downloadCertificatePdf = async ({
    certificateContent,
    client,
  }: DownloadCertificatePdfParams) => {
    const { lotnumber, owner, term, type, unit } = certificateContent

    try {
      setPDFLoading(true)

      const { errors, data } = await client.query({
        query: PdfDownloadDocument,

        variables: {
          input: {
            unit,
            owner,
            term,
            type,
            lotnumber,
          },
        },
      })

      if (errors || !data || data.error) throw errors

      downloadPDF(data.pdfDownload, term)
    } catch (error) {
      if (
        (type === CertificateType.Coa || type === CertificateType.Coo) &&
        setCertificateNotFoundModal
      ) {
        setCertificateNotFoundModal(type)
      } else {
        setGlobalSnackbarState({
          open: true,
          message: intl.formatMessage(
            {
              id: 'NO_PRODUCT_DOC_ONLINE',
              defaultMessage:
                'In some cases, our product documentation may not be available online. Please {contactUs} to request a copy.',
            },
            {
              contactUs: (
                <Linq href={support.customerSupport()}>
                  <FormattedMessage
                    id="CONTACT_US_LOWERCASE"
                    defaultMessage="contact us"
                  />
                </Linq>
              ),
            }
          ),
          variant: SnackbarType.Error,
        })
      }
    } finally {
      setPDFLoading(false)
    }
  }

  const showCertificatePdf = ({
    certificateContent,
    isCOA,
  }: ShowCertificatePdfParams) => {
    const { brand, lotNumber, productNumber } = certificateContent
    // This builds a certificate in the certificate route with the string of JSON mapped to testSpecifications
    const certificateRoute = isCOA ? certificateCOARoute : certificateCOORoute
    const localizedUrl = getLocalizedUrl(
      userSession,
      certificateRoute.index(brand, productNumber || '', lotNumber || '')
    )

    window.open(localizedUrl.as, '_blank')
  }

  return {
    downloadCertificatePdf,
    showCertificatePdf,
    pdfLoading,
    certificateNotFoundModal,
    handlePDFLoading: setPDFLoading,
    handleCertificateNotFoundModal: setCertificateNotFoundModal,
  }
}
